import React, { memo, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { currentStepSelector, formSummarySelector } from '../../../store/modules/steps/selectors';
import { CustomSelectType, CustomSelectTypeOptions, FormStep, FormStepProps, StepsState } from '../../../model/global';
import CustomFormField from '../../FormControls/CustomFormField';
import { getFormControlInitialState, getFormSummaryField } from '../../utils';
import { CustomFormFieldState } from '../../FormControls/CustomFormField/CustomFormField';
import { stepsActions } from '../../../store/modules/steps/reducer';

const ProductCategory: React.FC<FormStepProps> = ({ onStepControlsChange, data: { stepTemplate } }) => {

	const dispatch = useDispatch()
	const step = useSelector(currentStepSelector) as FormStep<'main'>
	const summary = useSelector(formSummarySelector) as StepsState['formSummary']
	const stepErrors = summary.validationErrors[step.stepTemplate];
	const mainField = step.stepData.fields.main

	const fields: {
		productCategory: CustomFormFieldState | undefined,
		productRelated: CustomFormFieldState | undefined,
		serialNumber: CustomFormFieldState | undefined,
		orderNumber: CustomFormFieldState | undefined,
		installationYear: CustomFormFieldState | undefined,
	} = useMemo(
		() => {
			return {
				productCategory: getFormControlInitialState('product_category', mainField),
				productRelated: getFormControlInitialState('product_related', mainField),
				serialNumber: getFormControlInitialState('serial_number', mainField),
				orderNumber: getFormControlInitialState('order_number', mainField),
				installationYear: getFormControlInitialState('installation_year', mainField),
			}
		},
		[mainField]
	);

	// initialise the step data once
	useEffect(() => {
		// don't re-init fields
		if (summary.initialized[stepTemplate]) return;

		const initialData: Record<string, CustomFormFieldState> = {};

		// fill in state with initial field values and validity
		for (const [key, element] of Object.entries(fields)) {
			if (!element || !element.name) continue;

			const invalid = element.value === '' && element.required === true
			initialData[element.name] = {
				name: element.name as string,
				originalField: element.originalField,
				value: element.value,
				required: !!element.required,
				invalidReason: element.invalidReason || '',
				valid: !invalid,
			};
		}

		dispatch(stepsActions.initializeStepPayloadValues({
			stepTemplate,
			stepPayload: initialData,
			initialize: true,
		}));


	}, [dispatch, fields, stepTemplate, summary.initialized]);

	const fieldChangeHandler = (event: CustomFormFieldState): void => {
		dispatch(stepsActions.updateStepPayloadValues({
			stepTemplate,
			stepPayload: event,
		}));
	}

	const categoryChangeHandler = (selectField: CustomFormFieldState): void => {
		selectField.value // heizsysteme
		const field = selectField.originalField as CustomSelectType;
		const groupItem = field.optionsGroup
			?.map(o => o.options)
			?.find(g => {
				if (!g) return undefined
				const exists = g.findIndex(o => o.value === selectField.value)
				return exists > -1 ? g[exists] : undefined
			})

		const optionItems: CustomSelectTypeOptions[] | undefined = groupItem && groupItem.filter(g => g.value === selectField.value)
		if (!optionItems) return

		// get the list of form controls which become required, now that the product category has been chosen
		// this list is in the data feed
		const additionalControls: CustomSelectTypeOptions['requiredAdditionalControls'] = optionItems[0].requiredAdditionalControls || [];

		// handle required state for serial and order numbers
		// ...

		// get controls from redux
		const relatedProductField = getFormSummaryField(summary, step.stepTemplate, 'product_related');
		const serialNumberField = getFormSummaryField(summary, step.stepTemplate, 'serial_number');
		const orderNumberField = getFormSummaryField(summary, step.stepTemplate, 'order_number');

		// make a copy as redux vals are readonly
		const newRelatedProductField = JSON.parse(JSON.stringify(relatedProductField)) as CustomFormFieldState
		const newSerialNumber = JSON.parse(JSON.stringify(serialNumberField)) as CustomFormFieldState
		const newOrderNumber = JSON.parse(JSON.stringify(orderNumberField)) as CustomFormFieldState

		// require all 3 form controls for logic after
		if (!serialNumberField || !orderNumberField || !relatedProductField) return;

		// reset serial and order number
		newSerialNumber.value = '';
		newSerialNumber.required = false;
		newSerialNumber.valid = true;

		newOrderNumber.value = '';
		newOrderNumber.required = false;
		newOrderNumber.valid = true;

		if (additionalControls.length > 0) {
			// for each requiredAdditionalControls, get the form control and set the required=true
			additionalControls.forEach(controlName => {
				if (controlName === 'serial_number') {
					newSerialNumber.value = '';
					newSerialNumber.required = true;
					newSerialNumber.valid = false;
				}
				if (controlName === 'order_number') {
					newOrderNumber.value = '';
					newOrderNumber.required = true;
					newOrderNumber.valid = false;
				}
			})
		}

		// build the list of options for the related product dropdown
		const relatedProductOptions = field.options.filter(o => o.groupParent === selectField.value);
		// inject options into control data - Note: they will be rendered before the originalField.options
		newRelatedProductField.options = relatedProductOptions
		// newRelatedProductField.value = '' // set the value back to empty
		newRelatedProductField.value = relatedProductOptions.length > 0 ? relatedProductOptions[0].value : '' // set the value to the first item
		// newRelatedProductField.valid = false // reset validity
		newRelatedProductField.valid = newRelatedProductField.value !== '' // update validity

		dispatch(stepsActions.updateStepPayloadValues({
			stepTemplate,
			stepPayload: [selectField, newRelatedProductField, newSerialNumber, newOrderNumber],
		}));

	}

	if (!step) return null

	const sumStepTemp = (item: string) => summary.steps[stepTemplate][item]

	const productCategory = sumStepTemp('product_category') ? sumStepTemp('product_category') : undefined;
	const productRelated = sumStepTemp('product_related') ? sumStepTemp('product_related') : undefined;
	const serialNumber = sumStepTemp('serial_number') ? sumStepTemp('serial_number') : undefined;
	const orderNumber = sumStepTemp('order_number') ? sumStepTemp('order_number') : undefined;
	const installationYear = sumStepTemp('installation_year') ? sumStepTemp('installation_year') : undefined;

	const attributeOBJ = (item: any, handler: any) => {
		return ({
			field: item,
			fieldErrors: stepErrors,
			onFieldChange: (e: any) => handler(e)
		})
	}

	return (
		<div className='uk-grid-medium uk-grid uk-grid-stack'>
			{/* product category * */}
			{productCategory && <CustomFormField {...attributeOBJ(productCategory, categoryChangeHandler)} />}

			{/* product (related to product category selection!) (This will be in a JSON (not an Ajax request)) */}
			{productRelated && <CustomFormField {...attributeOBJ(productRelated, fieldChangeHandler)} />}

			{/* (* Note: Based on the main product category, the serial number and/or order number is/are required, or not) */}
			{serialNumber && <CustomFormField {...attributeOBJ(serialNumber, fieldChangeHandler)} />}
			{orderNumber && <CustomFormField {...attributeOBJ(orderNumber, fieldChangeHandler)} />}
			{installationYear && <CustomFormField {...attributeOBJ(installationYear, fieldChangeHandler)} />}
		</div>
	);
};

export default memo(ProductCategory);
